import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Play, Pause, Maximize, Volume2, VolumeX } from 'lucide-react';

/* ─── URL parsing ─────────────────────────────────────────────── */
export function extractYouTubeId(url) {
  if (!url || typeof url !== 'string') return null;
  const s = url.trim();
  let m = s.match(/youtu\.be\/([A-Za-z0-9_-]{11})/); if (m) return m[1];
  m = s.match(/[?&]v=([A-Za-z0-9_-]{11})/);          if (m) return m[1];
  m = s.match(/youtube\.com\/(?:embed|shorts|live|v)\/([A-Za-z0-9_-]{11})/); if (m) return m[1];
  return null;
}
export function isYouTubeUrl(url) { return !!extractYouTubeId(url); }

/**
 * YouTubeEmbed — brand-free, click-proof player with our own controls.
 *  • Fills the frame (no black bars): the iframe is scaled up so YouTube's top
 *    title bar and bottom info strip are cropped OFF-frame — no mask bands.
 *  • Shows the CMS-uploaded poster until the video actually starts playing,
 *    and again once it ends (never YouTube's end screen / related videos).
 *  • Autoplays muted; loop off so the poster returns at the end.
 *  • One centred, round control group: Play/Pause + Fullscreen. Hidden while
 *    playing, revealed on hover / tap.
 *  • A click-shield swallows every tap so it can never redirect to YouTube.
 */
export default function YouTubeEmbed({
  src, poster, aspect = '16/9', fill = false, className = '', style = {}, testId = 'yt-embed',
}) {
  const id = extractYouTubeId(src);
  const containerRef = useRef(null);
  const iframeRef = useRef(null);
  const hideTimerRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [started, setStarted] = useState(false);   // has the video begun (poster clears)
  const [muted, setMuted] = useState(true);         // autoplay requires muted start
  const [showCtl, setShowCtl] = useState(false);

  const send = useCallback((func, args = []) => {
    const w = iframeRef.current?.contentWindow;
    if (!w) return;
    w.postMessage(JSON.stringify({ event: 'command', func, args }), '*');
  }, []);

  /* Listen to the YouTube IFrame API to sync the play/pause button. */
  useEffect(() => {
    const onMsg = (e) => {
      if (typeof e.data !== 'string') return;
      let d; try { d = JSON.parse(e.data); } catch { return; }
      if (d?.event === 'onReady') {
        iframeRef.current?.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'addEventListener', args: ['onStateChange'] }), '*');
      }
      const state = d?.info?.playerState;
      if (state === 1) { setStarted(true); setPlaying(true); }
      else if (state === 2) { setPlaying(false); }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  /* Fallback: clear the poster shortly after mount in case autoplay state
     messages don't arrive — the video is playing muted behind it by then. */
  useEffect(() => {
    const t = setTimeout(() => setStarted(true), 1400);
    return () => clearTimeout(t);
  }, []);

  const revealCtl = useCallback((sticky = false) => {
    setShowCtl(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (!sticky) hideTimerRef.current = setTimeout(() => setShowCtl(false), 2200);
  }, []);
  useEffect(() => () => { if (hideTimerRef.current) clearTimeout(hideTimerRef.current); }, []);

  const play = useCallback(() => {
    send('playVideo'); setPlaying(true); setStarted(true); revealCtl();
  }, [send, revealCtl]);

  const togglePlay = useCallback((e) => {
    e?.stopPropagation?.();
    if (playing) { send('pauseVideo'); setPlaying(false); revealCtl(true); }
    else play();
  }, [playing, send, play, revealCtl]);

  const goFullscreen = useCallback((e) => {
    e?.stopPropagation?.();
    const el = containerRef.current;
    if (!el) return;
    const req = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
    if (req) req.call(el);
  }, []);

  const toggleMute = useCallback((e) => {
    e?.stopPropagation?.();
    if (muted) { send('unMute'); send('setVolume', [100]); setMuted(false); }
    else { send('mute'); setMuted(true); }
    revealCtl(!playing);
  }, [muted, playing, send, revealCtl]);

  if (!id) return null;

  const params = new URLSearchParams({
    autoplay: '1', mute: '1', loop: '1', playlist: id,
    controls: '0', showinfo: '0', modestbranding: '1', rel: '0',
    iv_load_policy: '3', fs: '0', disablekb: '1',
    playsinline: '1', cc_load_policy: '0', enablejsapi: '1',
    origin: typeof window !== 'undefined' ? window.location.origin : '',
  });
  const embedUrl = `https://www.youtube-nocookie.com/embed/${id}?${params.toString()}`;

  const wrapStyle = fill
    ? { position: 'absolute', inset: 0, overflow: 'hidden', background: '#0e0b08', ...style }
    : { position: 'relative', aspectRatio: aspect.replace('/', ' / '), overflow: 'hidden', background: '#0e0b08', ...style };

  const posterVisible = poster && !started;

  return (
    <div
      ref={containerRef}
      className={`yt-embed ${className}`}
      style={wrapStyle}
      data-testid={testId}
      onMouseEnter={() => revealCtl(true)}
      onMouseLeave={() => { if (playing) setShowCtl(false); }}
    >
      {/* Scaled ~136% so YT's title bar + bottom strip crop OFF-frame → the
          video fills edge-to-edge with no black bands and no branding. */}
      <iframe
        ref={iframeRef}
        title="video"
        src={embedUrl}
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
        allowFullScreen
        referrerPolicy="no-referrer-when-downgrade"
        frameBorder="0"
        style={{
          position: 'absolute', top: '50%', left: '50%',
          width: '136%', height: '136%',
          transform: 'translate(-50%, -50%)',
          border: 0, pointerEvents: 'none',
        }}
        data-testid={`${testId}-iframe`}
      />

      {/* CMS poster — shown before first play and after the video ends. */}
      {posterVisible && (
        <img
          src={poster}
          alt=""
          className="yt-poster"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 6 }}
          data-testid={`${testId}-poster`}
        />
      )}

      {/* Click-shield — swallows every tap so YouTube never sees a gesture and
          the player can never redirect. Tap reveals the control. */}
      <div
        onClick={() => revealCtl(true)}
        onTouchStart={(e) => { e.stopPropagation(); revealCtl(true); }}
        style={{ position: 'absolute', inset: 0, zIndex: 7, cursor: 'pointer', touchAction: 'none', WebkitTapHighlightColor: 'transparent' }}
        data-testid={`${testId}-shield`}
      />

      {/* Single centred round control group: Play/Pause + Fullscreen.
          Auto-hidden while playing; shown when paused/ended/hovered/tapped. */}
      <div
        className={`yt-controls ${(showCtl || !playing) ? 'shown' : ''}`}
        style={{ zIndex: 8 }}
        data-testid={`${testId}-controls`}
      >
        <button type="button" className="yt-round" onClick={togglePlay}
          aria-label={playing ? 'Pause' : 'Play'} data-testid={`${testId}-btn-play`}>
          {playing ? <Pause size={22} strokeWidth={2} /> : <Play size={22} strokeWidth={2} style={{ marginLeft: 3 }} />}
        </button>
        <button type="button" className="yt-round" onClick={toggleMute}
          aria-label={muted ? 'Unmute' : 'Mute'} data-testid={`${testId}-btn-mute`}>
          {muted ? <VolumeX size={20} strokeWidth={2} /> : <Volume2 size={20} strokeWidth={2} />}
        </button>
        <button type="button" className="yt-round" onClick={goFullscreen}
          aria-label="Full screen" data-testid={`${testId}-btn-fs`}>
          <Maximize size={20} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
